import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { Shield, FileText, AlertTriangle } from 'lucide-react';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useSiteData } from '@/hooks/useSiteData';

export function Rules() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();

  const ruleCategories = [
    {
      title: "Range Rules",
      description: "General shooting range safety and operational guidelines",
      pdfUrl: "/pdf/RangeRules-20220414.pdf",
      icon: Shield,
      highlights: [
        "Always treat firearms as loaded",
        "Never point muzzle at anything you don't intend to destroy", 
        "Keep finger off trigger until ready to shoot",
        "Be sure of your target and what's beyond it"
      ]
    },
    {
      title: "Archery Range Rules", 
      description: "Specific safety protocols for archery activities",
      pdfUrl: "/pdf/ArcheryRangeRules-20230221.pdf",
      icon: FileText,
      highlights: [
        "Inspect arrows before each use",
        "Shoot only when range is clear",
        "Retrieve arrows only when all shooting has stopped",
        "Proper backstop and target setup required"
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Rules & Safety - {siteData.title}</title>
        <meta name="description" content="Safety rules and regulations for Wayland Rod and Gun Club facilities. Download range rules and archery guidelines to ensure safe participation." />
        <meta name="keywords" content="safety rules, range rules, archery rules, regulations, firearms safety, Wayland Rod Gun Club" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              Rules & Safety Guidelines
            </h1>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto leading-relaxed">
              Safety is our top priority. Please review all applicable rules before using club facilities.
            </p>
          </div>

          {/* Safety Alert */}
          <div className="card-professional bg-amber-50 border-l-4 border-l-amber-500">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-amber-800 mb-2">
                  Important Safety Notice
                </h2>
                <p className="text-amber-700 leading-relaxed">
                  All members and guests must follow club safety rules at all times. 
                  Violation of safety rules may result in immediate suspension of privileges.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {ruleCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div key={index} className="card-professional">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-display font-semibold text-secondary">
                        {category.title}
                      </h2>
                      <p className="text-foreground/60 text-sm">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                      Key Safety Points
                    </h3>
                    <ul className="space-y-2">
                      {category.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                          <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t border-border/50">
                    <a
                      href={category.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium transition-colors text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Download Complete Rules (PDF)
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="card-professional bg-muted/20">
            <div className="text-center">
              <h2 className="text-2xl font-display font-semibold text-secondary mb-4">
                Firearms Safety Training
              </h2>
              <p className="text-foreground/80 max-w-3xl mx-auto leading-relaxed mb-6">
                We offer Massachusetts State Police certified Basic Firearms Safety courses periodically. 
                These courses are essential for new members and beneficial for all skill levels.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/events" 
                  className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-semibold transition-colors"
                >
                  View Training Schedule
                </a>
                <a 
                  href="/contact" 
                  className="inline-flex items-center px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-lg font-semibold transition-colors"
                >
                  Contact for More Info
                </a>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}